import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, Pencil } from "lucide-react";
import { CandidateComment } from "@/types/candidate";
import { AddCommentModal } from "./AddCommentModal";
import { formatDistanceToNow } from "date-fns";
import { triggerReanalysis } from "@/utils/candidate/ai";
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from "@/components/ui/pagination";
import { useTranslation } from "react-i18next";
import { getUserDisplayName } from "@/utils/userUtils";
import { CommentContent } from "@/components/comments/CommentContent";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface CandidateCommentsProps {
  comments: CandidateComment[];
  candidateId: string;
  cv?: string | null;
  handleAddComment: (content: string) => Promise<void>;
  handleDeleteComment: (commentId: string) => Promise<void>;
  handleUpdateComment: (commentId: string, content: string) => Promise<void>;
  isLoading: boolean;
}

export const CandidateComments = ({
  comments,
  candidateId,
  cv,
  handleAddComment,
  handleDeleteComment,
  handleUpdateComment,
  isLoading
}: CandidateCommentsProps) => {
  const { t } = useTranslation();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState<CandidateComment | null>(null);
  const [commentToEdit, setCommentToEdit] = useState<CandidateComment | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Debug logging for props
  //console.log("🔍 CandidateComments props:", { candidateId, cv, commentsCount: comments.length });
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 4;
  
  // Calculate pagination
  const totalPages = Math.ceil(comments.length / itemsPerPage);
  const indexOfLastComment = currentPage * itemsPerPage;
  const indexOfFirstComment = indexOfLastComment - itemsPerPage;
  const currentComments = comments
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    .slice(indexOfFirstComment, indexOfLastComment);

  const onAddComment = async (content: string) => {
    //console.log("🚀🚀🚀 ADD COMMENT FUNCTION CALLED 🚀🚀🚀", { candidateId, cv, content });
    
    try {
      //console.log("📝 About to call handleAddComment...");
      await handleAddComment(content);
      //console.log("✅ handleAddComment completed successfully");
      
      // Always trigger reanalysis regardless of CV presence
      //console.log("🔄 Triggering reanalysis for candidate...", { candidateId, cv });
      setIsAnalyzing(true);
      setIsAddModalOpen(false);
      await triggerReanalysis(candidateId, cv, content);
      //console.log("✅ triggerReanalysis completed");
      setIsAnalyzing(false);
    } catch (error) {
      //console.error("❌ Error adding comment:", error);
      setIsAnalyzing(false);
    }
  };

  const onEditComment = async (content: string) => {
    if (!commentToEdit) return;
    
    try {
      await handleUpdateComment(commentToEdit.id, content);
      setCommentToEdit(null);
    } catch (error) {
      console.error("Error updating comment:", error);
    }
  };

  const onDeleteComment = async () => {
    if (!commentToDelete) return;
    
    try {
      setIsDeleting(true);
      await handleDeleteComment(commentToDelete.id);
      setCommentToDelete(null);
    } catch (error) {
      console.error("Error deleting comment:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle pagination
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Direct scroll without any transition
    const scrollableParent = document.querySelector('.overflow-y-auto') as HTMLElement;
    if (scrollableParent) {
      // Force immediate scroll without animation
      scrollableParent.style.scrollBehavior = 'auto';
      scrollableParent.scrollTop = 0;
      scrollableParent.style.scrollBehavior = '';
    }
  };

  return (
    <div className="flex flex-col h-full space-y-4">
      <div className="flex justify-end items-center mb-4">
        <Button 
          onClick={() => setIsAddModalOpen(true)}
          disabled={isAnalyzing}
          className="bg-[#5a7ebf] hover:bg-[#5a7ebf]/90 text-white flex items-center gap-2 px-4 py-2"
        >
          <Plus className="h-5 w-5" />
          {isAnalyzing ? 'Analyzing...' : t("companies.comments.addComment")}
        </Button>
      </div>
      
      {isLoading ? (
        <div className="flex items-center justify-center h-40">
          <div className="animate-spin h-5 w-5 border-2 border-digger-blue border-t-transparent rounded-full"></div>
        </div>
      ) : comments.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-sm">
          <p className="text-gray-500 dark:text-gray-400">{t("companies.comments.noComments")}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {currentComments.map(comment => (
            <div key={comment.id} className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-sm">
              <div className="flex justify-between mb-2">
                <span className={`font-medium ${getUserDisplayName(comment).isItalic ? 'italic' : ''}`}>
                  {getUserDisplayName(comment).name}
                </span>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {new Date(comment.created_at).toLocaleString()}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setCommentToEdit(comment)}
                    className="h-6 w-6 p-0 text-gray-400 hover:text-blue-500"
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setCommentToDelete(comment)}
                    className="h-6 w-6 p-0 text-gray-400 hover:text-red-500"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <CommentContent content={comment.content} />
            </div>
          ))}
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="sticky bottom-0 z-10 bg-white dark:bg-gray-900">
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious 
                      onClick={() => handlePageChange(currentPage - 1)}
                      className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                    />
                  </PaginationItem>
                  
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <PaginationItem key={page}>
                      <PaginationLink
                        isActive={page === currentPage}
                        onClick={() => handlePageChange(page)}
                        className="cursor-pointer"
                      >
                        {page}
                      </PaginationLink>
                    </PaginationItem>
                  ))}
                  
                  <PaginationItem>
                    <PaginationNext 
                      onClick={() => handlePageChange(currentPage + 1)}
                      className={currentPage === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          )}
        </div>
      )}
      
      <AddCommentModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSave={onAddComment}
      />

      <AddCommentModal
        isOpen={!!commentToEdit}
        onClose={() => setCommentToEdit(null)}
        onSave={onEditComment}
        initialContent={commentToEdit?.content || ""}
        isEditing={true}
      />

      <AlertDialog open={!!commentToDelete} onOpenChange={(open) => !open && setCommentToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Comment</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this comment? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={onDeleteComment}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
